import apiService from './apiService';

// TypeScript interfaces
export interface InvoiceItem {
  id?: string;
  invoiceId?: string;
  description: string; // Free-text item description (required)
  itemCode?: string; // Optional item code
  quantity: number;
  unit?: string; // unit(s), Rack(s), Store, Month(s), Order(s), Hour(s)
  unitPrice: number;
  discount?: number;
  discountType?: 'fixed' | 'percentage';
  taxRate?: number;
  taxAmount?: number;
  lineTotal: number;
  accountId?: string; // Revenue account for this line item
  departmentId?: string; // Analytic dimension; flows to the JE line on post
  departmentName?: string | null;
  sortOrder?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface InvoiceOption {
  id?: string;
  invoiceId?: string;
  category?: string;
  subcategory?: string;
  description: string;
  itemCode?: string;
  quantity: number;
  unit?: string;
  unitPrice: number;
  discount?: number;
  discountType?: 'fixed' | 'percentage';
  lineTotal: number;
  accountId?: string;
  sortOrder?: number;
  createdAt?: string;
}

export interface Invoice {
  id: string;
  invoiceNo: string;
  customerId: string;
  userId: string;
  companyId: string;
  invoiceDate?: string;
  totalAmount: number;
  currency: string; // HKD, USD, CNY, etc.
  status: 'DRAFT' | 'CONFIRM' | 'SENT' | 'PAID' | 'OVERDUE' | 'CANCELLED';
  dueDate?: string;
  billReference?: string;
  paymentTerms?: string;
  paymentTermsText?: string;
  termsConditions?: string;
  deliveryAddress?: string;
  billingAddress?: string;
  contactPerson?: string;
  contactEmail?: string;
  contactPhone?: string;
  journalEntryId?: string;
  createdAt: string;
  updatedAt: string;
  items: InvoiceItem[];
  options?: InvoiceOption[];
  customer?: {
    id: string;
    name: string;
    email?: string;
  };
  user?: {
    id: string;
    name: string;
    email: string;
  };
  company?: {
    id: string;
    name: string;
    address?: string;
  };
}

export interface CreateInvoiceInput {
  customerId: string;
  invoiceNo?: string;
  invoiceDate?: string;
  totalAmount: number;
  currency?: string;
  exchangeRate?: number;
  status?: 'DRAFT' | 'CONFIRM' | 'SENT';
  dueDate?: string;
  billReference?: string;
  paymentTerms?: string;
  paymentTermsText?: string;
  termsConditions?: string;
  deliveryAddress?: string;
  billingAddress?: string;
  contactPerson?: string;
  contactEmail?: string;
  contactPhone?: string;
  items: Omit<InvoiceItem, 'id' | 'invoiceId' | 'createdAt' | 'updatedAt'>[];
  options?: Omit<InvoiceOption, 'id' | 'invoiceId' | 'createdAt'>[];
}

export interface UpdateInvoiceInput {
  customerId?: string;
  invoiceNo?: string;
  invoiceDate?: string;
  totalAmount?: number;
  currency?: string;
  exchangeRate?: number;
  status?: string;
  dueDate?: string;
  billReference?: string;
  paymentTerms?: string;
  paymentTermsText?: string;
  termsConditions?: string;
  deliveryAddress?: string;
  billingAddress?: string;
  contactPerson?: string;
  contactEmail?: string;
  contactPhone?: string;
  items?: Omit<InvoiceItem, 'id' | 'invoiceId' | 'createdAt' | 'updatedAt'>[];
  options?: Omit<InvoiceOption, 'id' | 'invoiceId' | 'createdAt'>[];
}

export interface InvoiceFilters {
  customerId?: string;
  /** Where the document is: DRAFT | CONFIRM | SENT | CANCELLED. Several may be selected. */
  status?: string[];
  /** Whether the money arrived: PAID | UNPAID | OVERDUE. Several may be selected. */
  payment?: string[];
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}

export interface PostInvoiceInput {
  accountsReceivableAccountId: string;
  revenueAccountId: string;
  journalId: string;
}

export interface InvoiceStats {
  totalInvoices: number;
  draftInvoices: number;
  sentInvoices: number;
  paidInvoices: number;
  overdueInvoices: number;
  totalAmount: number;
  paidAmount: number;
  outstandingAmount: number;
}

// AR Aging Report types
export interface ARAgingReportSummary {
  current: number;
  days30: number;
  days60: number;
  days90Plus: number;
  total: number;
}

export interface ARAgingReportByCustomer {
  customerId: string;
  customerName: string;
  customerCode: string;
  current: number;
  days30: number;
  days60: number;
  days90Plus: number;
  total: number;
}

export interface ARAgingReportDetail {
  invoiceId: string;
  invoiceNo: string;
  customerId: string;
  customerName: string;
  invoiceDate: string;
  dueDate: string;
  amountTotal: number;
  amountResidual: number;
  daysOverdue: number;
  agingBucket: 'current' | 'days30' | 'days60' | 'days90Plus';
}

export interface ARAgingReportData {
  summary: ARAgingReportSummary;
  byCustomer: ARAgingReportByCustomer[];
  details: ARAgingReportDetail[];
}

// Customer Statement types
export interface CustomerStatementTransaction {
  date: string;
  type: 'INVOICE' | 'PAYMENT' | 'CREDIT_NOTE';
  reference: string;
  description: string;
  debit: number;
  credit: number;
  balance: number;
}

export interface CustomerStatementData {
  customer: {
    id: string;
    name: string;
    code: string;
    email: string | null;
    phone: string | null;
    address: string | null;
  };
  period: {
    fromDate: string;
    toDate: string;
  };
  openingBalance: number;
  transactions: CustomerStatementTransaction[];
  closingBalance: number;
  summary: {
    totalInvoices: number;
    totalPayments: number;
    totalCredits?: number;
    totalDue: number;
  };
}

class InvoiceService {
  private baseUrl = '/accounting/invoices';

  /**
   * Get all invoices with optional filters
   */
  async getAllInvoices(filters?: InvoiceFilters): Promise<Invoice[]> {
    const params = new URLSearchParams();
    params.append('limit', '1000'); // Get more invoices by default
    if (filters?.customerId) params.append('customerId', filters.customerId);
    // Comma-separated, matching the list the controller parses.
    if (filters?.status?.length) params.append('status', filters.status.join(','));
    if (filters?.payment?.length) params.append('payment', filters.payment.join(','));
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    if (filters?.search) params.append('search', filters.search);

    const queryString = params.toString();
    const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;

    const response = await apiService.get(url);
    // Backend returns { invoices: [], pagination: {} }
    return response.invoices || response.data || [];
  }

  /**
   * Get invoice by ID
   */
  async getInvoiceById(id: string): Promise<Invoice> {
    const response = await apiService.get(`${this.baseUrl}/${id}`);
    return response.data;
  }

  /**
   * Get invoice by invoice number
   */
  async getInvoiceByNumber(invoiceNo: string): Promise<Invoice> {
    const response = await apiService.get(`${this.baseUrl}/number/${invoiceNo}`);
    return response.data;
  }

  /**
   * Create a new invoice
   */
  async createInvoice(data: CreateInvoiceInput): Promise<Invoice> {
    const response = await apiService.post(this.baseUrl, data);
    return response.data;
  }

  /**
   * Update an existing invoice
   */
  async updateInvoice(id: string, data: UpdateInvoiceInput): Promise<Invoice> {
    const response = await apiService.put(`${this.baseUrl}/${id}`, data);
    return response.data;
  }

  /**
   * Post an invoice (DRAFT → SENT)
   * Creates a journal entry automatically
   */
  async postInvoice(id: string, data: PostInvoiceInput): Promise<Invoice> {
    const response = await apiService.post(`${this.baseUrl}/${id}/post`, data);
    return response.data;
  }

  /**
   * Confirm an invoice (DRAFT → CONFIRM).
   * Creates the journal entry now. Sending the email later only flips status to SENT.
   */
  async confirmInvoice(id: string, data: PostInvoiceInput): Promise<Invoice> {
    const response = await apiService.post(`${this.baseUrl}/${id}/confirm`, data);
    return response.data;
  }

  /**
   * Send the invoice to the customer by email with the PDF attached.
   */
  async sendInvoiceEmail(
    id: string,
    data: {
      to: string | string[];
      cc?: string | string[];
      bcc?: string | string[];
      subject?: string;
      body?: string;
      html?: string;
      /** Extra files, base64-encoded, on top of the generated invoice PDF. */
      attachments?: Array<{ filename: string; content: string; contentType?: string }>;
    }
  ): Promise<{ messageId: string; accepted: string[]; rejected: string[]; subject: string; to: string[] }> {
    const response = await apiService.post(`${this.baseUrl}/${id}/send-email`, data);
    return response.data;
  }

  /**
   * The addresses last used for this customer, so the send dialog can pre-fill
   * them. Returns null when nothing has been sent to them yet.
   */
  async getEmailDefaults(
    customerId: string
  ): Promise<{ to: string[]; cc: string[]; sentAt: string } | null> {
    const response: any = await apiService.get(
      `${this.baseUrl}/email-defaults?customerId=${encodeURIComponent(customerId)}`
    );
    return response?.data ?? response ?? null;
  }

  /**
   * Mark invoice as PAID
   */
  async markInvoiceAsPaid(
    id: string,
    data: {
      paymentAccountId: string;
      arAccountId: string;
      journalId: string;
      paymentDate?: string;
      paymentMethodId?: string;
      memo?: string;
    }
  ): Promise<Invoice> {
    const response = await apiService.post(`${this.baseUrl}/${id}/mark-paid`, data);
    return response.data;
  }

  /**
   * Mark invoice as OVERDUE
   */
  async markInvoiceAsOverdue(id: string): Promise<Invoice> {
    const response = await apiService.post(`${this.baseUrl}/${id}/mark-overdue`, {});
    return response.data;
  }

  /**
   * Cancel an invoice
   * Creates a reversing journal entry if posted
   */
  async cancelInvoice(id: string, data: PostInvoiceInput): Promise<Invoice> {
    const response = await apiService.post(`${this.baseUrl}/${id}/cancel`, data);
    return response.data;
  }

  /**
   * Delete an invoice
   */
  async deleteInvoice(id: string): Promise<void> {
    await apiService.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Get invoices by customer
   */
  async getInvoicesByCustomer(customerId: string): Promise<Invoice[]> {
    const response = await apiService.get(`${this.baseUrl}/customer/${customerId}`);
    return response.data;
  }

  /**
   * Get overdue invoices
   */
  async getOverdueInvoices(): Promise<Invoice[]> {
    const response = await apiService.get(`${this.baseUrl}/overdue`);
    return response.data;
  }

  /**
   * Get invoice statistics
   */
  async getInvoiceStats(): Promise<InvoiceStats> {
    const response = await apiService.get(`${this.baseUrl}/stats`);
    return response.data;
  }

  /**
   * Get AR Aging Report with 30/60/90+ day buckets
   */
  async getAgingReport(asOfDate?: string, invoiceDateFrom?: string): Promise<ARAgingReportData> {
    const qp = new URLSearchParams();
    if (asOfDate) qp.append('asOfDate', asOfDate);
    if (invoiceDateFrom) qp.append('invoiceDateFrom', invoiceDateFrom);
    const params = qp.toString() ? `?${qp}` : '';
    const response = await apiService.get(`${this.baseUrl}/aging-report${params}`);
    return response.data;
  }

  /**
   * Get Customer Statement
   */
  async getCustomerStatement(customerId: string, fromDate?: string, toDate?: string): Promise<CustomerStatementData> {
    const params = new URLSearchParams();
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await apiService.get(`/accounting/customers/${customerId}/statement${queryString}`);
    return response.data;
  }
}

export default new InvoiceService();